import { api, tokenManager } from "@/lib/api";

export interface InternalUser {
  id: string;
  username: string;
  email: string;
  displayName: string;
  roles: ("super_admin" | "admin" | "coach_manager" | "influencer")[];
  status: "active" | "suspended" | "pending";
  lastLogin: string | null;
  createdAt: string;
  invitedAt?: string;
  /** Pending invites only. */
  expiresAt?: string;
  invitedBy?: string;
}

export interface InternalUsersFilters {
  page?: number;
  limit?: number;
  role?: string;
  status?: string;
  search?: string;
}

export interface PaginatedInternalUsersResponse {
  users: InternalUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statusCounts: {
    active: number;
    pending: number;
    suspended: number;
  };
}

export interface VerifyInviteResponse {
  message: string;
  invitation: {
    email: string;
    role: string;
    name: string;
  };
}

export interface InviteUserRequest {
  name: string;
  email: string;
  role: "admin" | "coach_manager";
}

export interface InviteUserResponse {
  message: string;
  invitationId?: string;
}

export interface AcceptInternalInvitationRequest {
  name: string;
  email: string;
  role: "admin" | "coach_manager";
  password: string;
  bio?: string;
  token: string;
  avatar?: File;
}

export interface AcceptInternalInvitationResponse {
  message: string;
  userId: string;
}

export interface UpdateInternalProfileRequest {
  name?: string;
  email?: string;
  avatar?: File;
  removeAvatar?: boolean;
  role?: "admin" | "coach_manager";
}

export interface UpdateInternalProfileResponse {
  message: string;
  accessToken?: string;
  user: {
    id: string;
    name: string;
    email: string;
    roles: string[];
    avatarUrl: string | null;
  };
}

export interface RequestPasswordResetRequest {
  email: string;
}

export interface VerifyResetTokenResponse {
  message: string;
  user: {
    email: string;
    name: string;
  };
}

export interface CompletePasswordResetRequest {
  password: string;
  token: string;
}

export interface UpdateUserRoleRequest {
  role: "admin" | "coach_manager" | "coach";
}

export interface UpdateUserRoleResponse {
  message: string;
  user: {
    id: string;
    name: string;
    email: string;
    roles: string[];
  };
  previousRole: string;
  newRole: string;
  reason?: string;
}

export interface RemoveUserRoleRequest {
  role: "admin" | "coach_manager" | "coach" | "influencer";
}

export interface RemoveUserRoleResponse {
  message: string;
  user: {
    id: string;
    name: string;
    email: string;
    roles: string[];
  };
}

export class InternalUsersService {
  private static readonly BASE_URL = "/admin/internal-users";

  static async getInternalUsers(
    filters?: InternalUsersFilters,
  ): Promise<PaginatedInternalUsersResponse> {
    const params = new URLSearchParams();

    if (filters?.page) {
      params.append("page", String(filters.page));
    }
    if (filters?.limit) {
      params.append("limit", String(filters.limit));
    }
    if (filters?.role && filters.role !== "all") {
      params.append("role", filters.role);
    }
    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }
    if (filters?.search && filters.search.trim()) {
      params.append("search", filters.search.trim());
    }

    const queryString = params.toString();
    const url = queryString ? `${this.BASE_URL}?${queryString}` : this.BASE_URL;

    return api.get<PaginatedInternalUsersResponse>(url);
  }

  static async updateInternalProfile(
    data: UpdateInternalProfileRequest,
  ): Promise<UpdateInternalProfileResponse> {
    const formData = new FormData();

    if (data.name) {
      formData.append("name", data.name);
    }

    if (data.email) {
      formData.append("email", data.email);
    }

    if (data.role) {
      formData.append("role", data.role);
    }

    if (data.avatar) {
      formData.append("avatar", data.avatar);
    }

    if (data.removeAvatar) {
      formData.append("removeAvatar", "true");
    }

    const res = await api.patch<UpdateInternalProfileResponse>(
      "/admin/profile",
      formData,
    );
    // An email change revokes all sessions; the server re-issues one for us.
    if (res.accessToken) tokenManager.set(res.accessToken);
    return res;
  }

  static async verifyInvite(token: string): Promise<VerifyInviteResponse> {
    return api.get<VerifyInviteResponse>(`/admin/invite/${token}`);
  }

  static async inviteInternalUser(
    data: InviteUserRequest,
  ): Promise<InviteUserResponse> {
    return api.post<InviteUserResponse>("/admin/invite", data);
  }

  static async acceptInternalInvitation(
    data: AcceptInternalInvitationRequest,
  ): Promise<AcceptInternalInvitationResponse> {
    const formData = new FormData();

    formData.append("name", data.name);
    formData.append("email", data.email);
    formData.append("password", data.password);
    formData.append("role", data.role);
    formData.append("token", data.token);
    formData.append("bio", data.bio || "");

    if (data.avatar) {
      formData.append("avatar", data.avatar);
    }

    const response = await api.post<{
      message: string;
      userId: string;
    }>("/admin/accept-internal-invitation", formData, true);
    return response;
  }

  static async suspendUser(
    userId: string,
    reason?: string,
  ): Promise<{ message: string }> {
    return api.patch<{ message: string }>(`/admin/users/${userId}/status`, {
      action: "suspend",
      reason,
    });
  }

  static async restoreUser(
    userId: string,
    reason?: string,
  ): Promise<{ message: string }> {
    return api.patch<{ message: string }>(`/admin/users/${userId}/status`, {
      action: "restore",
      reason,
    });
  }

  static async deleteUser(
    userId: string,
    reason?: string,
  ): Promise<{ message: string }> {
    return api.patch<{ message: string }>(`/admin/users/${userId}/status`, {
      action: "delete",
      reason,
    });
  }

  static async resendInvitation(
    invitationId: string,
  ): Promise<{ message: string }> {
    return api.post<{ message: string }>(
      `/admin/invite/resend/${invitationId}`,
    );
  }

  static async cancelInvitation(
    invitationId: string,
  ): Promise<{ message: string }> {
    return api.patch<{ message: string }>(
      `/admin/invite/${invitationId}/cancel`,
    );
  }

  /** EVO-88: proves the current password; revokes other sessions. */
  static async changeOwnPassword(data: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ message: string; accessToken?: string }> {
    const res = await api.patch<{ message: string; accessToken?: string }>(
      "/admin/profile/password",
      data,
    );
    // Every session was revoked; the server re-issues one for this tab.
    if (res.accessToken) tokenManager.set(res.accessToken);
    return res;
  }

  static async requestPasswordReset(
    data: RequestPasswordResetRequest,
  ): Promise<{ message: string }> {
    return api.post<{ message: string }>("/admin/reset-password", data);
  }

  // Self-service (unauthenticated) forgot-password request from the login page.
  // Always resolves with a generic message — never reveals account existence.
  static async forgotPassword(
    data: RequestPasswordResetRequest,
  ): Promise<{ message: string }> {
    return api.post<{ message: string }>("/admin/forgot-password", data);
  }

  static async verifyResetToken(
    token: string,
  ): Promise<VerifyResetTokenResponse> {
    return api.get<VerifyResetTokenResponse>(`/admin/reset-password/${token}`);
  }

  static async completePasswordReset(
    data: CompletePasswordResetRequest,
  ): Promise<{ message: string }> {
    return api.post<{ message: string }>(
      "/admin/reset-password/complete",
      data,
    );
  }

  static async updateUserRole(
    userId: string,
    data: UpdateUserRoleRequest,
  ): Promise<UpdateUserRoleResponse> {
    const response = await api.patch<UpdateUserRoleResponse>(
      `/admin/users/${userId}/role`,
      data,
    );
    return response;
  }

  static async removeUserRole(
    userId: string,
    data: RemoveUserRoleRequest,
  ): Promise<RemoveUserRoleResponse> {
    return api.delete<RemoveUserRoleResponse>(
      `/admin/users/${userId}/role`,
      data,
    );
  }
}
